import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, SectionLabel, GradientButton, GhostButton } from "@/components/primitives";
import { Copy, Key, Shield, User, Building2, Bell as BellIcon, Palette } from "lucide-react";

const TABS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "organization", label: "Organization", icon: Building2 },
  { id: "security", label: "Security", icon: Shield },
  { id: "api", label: "API Keys", icon: Key },
  { id: "notifications", label: "Notifications", icon: BellIcon },
  { id: "theme", label: "Theme", icon: Palette },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");
  const [saved, setSaved] = useState(false);
  const [apiKey] = useState("sk_live_sentinel_" + Math.random().toString(36).slice(2, 18));
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 3500); };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div><SectionLabel>Settings</SectionLabel><h1 className="mt-1 text-3xl font-bold tracking-tight">Workspace <span className="gradient-text">preferences</span></h1></div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        <GlassCard className="p-3">
          {TABS.map((t) => (
            <button data-testid={`settings-tab-${t.id}`} key={t.id} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl mb-1 text-sm border ${tab === t.id ? "bg-white/[0.06] border-white/10" : "border-transparent hover:bg-white/[0.04]"}`}>
              <t.icon className="h-4 w-4 text-cyan-300" /> {t.label}
            </button>
          ))}
        </GlassCard>

        <GlassCard className="p-6">
          {tab === "profile" && (
            <div className="space-y-4 max-w-xl">
              <h3 className="font-semibold">Profile</h3>
              <Field label="Full name" defaultValue={user?.name} testid="settings-name" />
              <Field label="Email" defaultValue={user?.email} testid="settings-email" disabled />
              <Field label="Role" defaultValue={user?.role} testid="settings-role" />
              <div className="flex gap-2"><GradientButton onClick={save} data-testid="settings-save">{saved ? "Saved ✓" : "Save changes"}</GradientButton><GhostButton>Cancel</GhostButton></div>
            </div>
          )}
          {tab === "organization" && (
            <div className="space-y-4 max-w-xl">
              <h3 className="font-semibold">Organization</h3>
              <Field label="Company" defaultValue={user?.company} testid="settings-company" />
              <Field label="Default workspace" defaultValue="Production" />
              <GradientButton onClick={save}>Save</GradientButton>
            </div>
          )}
          {tab === "security" && (
            <div className="space-y-4 max-w-xl">
              <h3 className="font-semibold">Security</h3>
              <Field label="Current password" type="password" testid="settings-current-pw" />
              <Field label="New password" type="password" testid="settings-new-pw" />
              <div className="text-xs text-slate-400">Two-factor authentication: <span className="text-cyan-300">Enabled (Authenticator)</span></div>
              <GradientButton onClick={save}>Update password</GradientButton>
            </div>
          )}
          {tab === "api" && (
            <div className="space-y-4 max-w-xl">
              <h3 className="font-semibold">API Keys</h3>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-between gap-3">
                <code className="font-mono text-xs text-cyan-300 truncate" data-testid="settings-api-key">{apiKey}</code>
                <button
title="Copy API Key"
onClick={() => {
    navigator.clipboard.writeText(apiKey);
    alert("API Key copied!");
}} className="p-1.5 rounded hover:bg-white/10 text-slate-400"><Copy className="h-4 w-4" /></button>
              </div>
              <div className="text-xs text-slate-400">Use this key in your CI/CD or platform integrations. Rotate every 90 days.</div>
            </div>
          )}
          {tab === "notifications" && (
            <div className="space-y-3 max-w-xl">
              <h3 className="font-semibold">Notifications</h3>
              {["Incident alerts","AI summaries","Weekly digest","Team mentions"].map((n, i) => (
                <label key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/10">
                  <span className="text-sm">{n}</span>
                  <input type="checkbox" defaultChecked={i < 3} className="accent-cyan-400" />
                </label>
              ))}
            </div>
          )}
          {tab === "theme" && (
            <div className="space-y-3 max-w-xl">
              <h3 className="font-semibold">Theme</h3>
              <div className="grid grid-cols-3 gap-3">
                {["Enterprise Noir","Aurora","Carbon"].map((t, i) => (
  <div
    key={t}
    title={i === 0 ? "Current theme" : "Coming Soon"}
    className={`p-4 rounded-2xl transition-all duration-300 ${
      i === 0
        ? "border border-cyan-400/50 bg-cyan-500/[0.05]"
        : "border border-white/10 bg-white/[0.03] opacity-80 cursor-not-allowed"
    }`}
  >
    <div className="h-16 rounded-xl bg-gradient-to-br from-cyan-400/30 via-blue-500/30 to-violet-500/30" />

    <div className="text-sm font-semibold mt-3">
      {t}
    </div>

    {i === 0 ? (
      <div className="mt-2 inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-300">
        ✓ Active
      </div>
    ) : (
      <div className="mt-2 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
        Coming Soon
      </div>
    )}
  </div>
))}
              </div>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

function Field({ label, defaultValue, type = "text", testid, disabled }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
      <input data-testid={testid} type={type} defaultValue={defaultValue} disabled={disabled} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm disabled:opacity-60" />
    </label>
  );
}
